import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Lock, ArrowRight } from "lucide-react";
import { MaskedLines } from "@/components/onboarding/MaskedText";
import { api, extractErrorMessage } from "@/lib/api";

const LOGO_URL =
  "https://sonarbanglahaat.com/wp-content/uploads/2025/12/cropped-Logo-Photoroom-1.png";

export default function AdminLogin() {
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/admin/login", { email, password });
      localStorage.setItem("sbh_admin_token", data.token);
      localStorage.setItem("sbh_admin_email", data.email);
      toast.success("Welcome back.");
      nav("/admin");
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-[color:var(--sbh-cream)] relative overflow-hidden flex items-center">
      <div className="grain fixed inset-0 pointer-events-none z-[5]" />
      <div className="w-full max-w-6xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-2 gap-16 items-center relative z-10">
        <div>
          <Link
            to="/"
            className="inline-flex items-center gap-3"
            data-testid="admin-login-logo-link"
          >
            <img
              src={LOGO_URL}
              alt="Sonar Bangla Haat"
              className="h-12 md:h-14 w-auto"
              draggable="false"
            />
          </Link>
          <div className="eyebrow mt-10">Sonar Bangla Haat · Private</div>
          <h1 className="mt-3 font-editorial text-[42px] md:text-[56px] leading-[1.05] tracking-[-0.01em] max-w-lg">
            <MaskedLines
              lines={["The keeper's",
                <span key="i" className="italic text-[color:var(--sbh-terracotta)]">quiet room.</span>]}
            />
          </h1>
          <p className="mt-6 max-w-md text-[15px] leading-relaxed text-[color:var(--sbh-ink-2)]">
            An internal, private dashboard for reviewing vendor applications and verification documents. Access is restricted.
          </p>
        </div>

        <motion.form
          onSubmit={submit}
          className="border border-[color:var(--sbh-border)] bg-[color:var(--sbh-cream)] p-8 md:p-12 relative"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.2 }}
          data-testid="admin-login-form"
        >
          <div className="flex items-center gap-3 mb-6">
            <Lock className="w-4 h-4 text-[color:var(--sbh-terracotta)]" strokeWidth={1.8} />
            <span className="eyebrow">Admin Sign In</span>
          </div>

          <label className="block mb-6">
            <span className="eyebrow text-[color:var(--sbh-ink-2)]">Email</span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="username"
              className="mt-2 w-full bg-transparent outline-none border-b border-[color:var(--sbh-border)] focus:border-[color:var(--sbh-terracotta)] py-3 text-[15px]"
              data-testid="admin-email-input"
            />
          </label>
          <label className="block mb-8">
            <span className="eyebrow text-[color:var(--sbh-ink-2)]">Password</span>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              className="mt-2 w-full bg-transparent outline-none border-b border-[color:var(--sbh-border)] focus:border-[color:var(--sbh-terracotta)] py-3 text-[15px]"
              data-testid="admin-password-input"
            />
          </label>

          <button
            type="submit"
            disabled={busy}
            className="btn-editorial w-full justify-center"
            data-testid="admin-login-btn"
          >
            {busy ? "Signing in…" : "Enter Dashboard"}
            <ArrowRight className="arrow w-4 h-4" strokeWidth={2} />
          </button>
        </motion.form>
      </div>
    </div>
  );
}
